<template>
  <config-base class="opc-ze" :saas-mode="saasMode" show-plan-details v-bind="$props" >
    <template slot="config">
      <!-- consumption mode -->
      <config-consumption-row
        v-if="saasMode === EventModeMap.PAY_PER_ATTENDEE"
        :ref="EventModeMap.PAY_PER_ATTENDEE"
        :plan-type="planType"
      />
      <!-- subscription mode -->
      <saas-capacity-config-list
        v-if="saasMode === EventModeMap.SUBSCRIPTION"
        :ref="EventModeMap.SUBSCRIPTION"
        :config-plan-type="planType"
        :config-capacity-key="capacityKey"
        :title="title"
      />
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapGetters, mapMutations } from 'vuex';
import ConfigList from '@/components/capacity/config-list';
import { BILLING_CYCLE, planTypes, ZOOM_SASS_ADDITIONAL_CATEGORIES } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/plans/EVENT_SAAS/config/base';
import planCommonMixin from '@/plans/EVENT_SAAS/mixin';
import { EventModeMap } from '@/plans/EVENT_SAAS/helper/constant';
import ConfigConsumptionRow from '@/plans/EVENT_SAAS/component/config-consumption-row';
import { pageConfig } from '@/common/service/page-config';
import { getPlanTotalLicense } from '@/common/service/validate';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { isBuyingWnE } from '@/plans/ZOOM_MEETING/common/utils';
import autoAddMixin from "@/components/capacity/mx/autoAdd";
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';


const SaasCapacityConfigList = {
  mixins: [ConfigList ,autoAddMixin],
  props: {
    title: String,
  },
  computed: {
    ...mapGetters(['basePlanTotalLicenses', 'getCurrentBasePlanInfo', 'getSelectedPlans']),
    // all plans under the current product type
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.configPlanType && p.isSaas);
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType]?.filter((p) => p.isSaas);
    },
    currentBasePlan() {
      return _.get(this.getCurrentBasePlanInfo, ["plans", 0], {});
    },
    mx_capacityTips() {
      return this.$t('billing.events.saas.capacityTips', [this.title]);
    },
    addAnotherCapacityCta() {
      return this.configPlanType === planTypes.EVENT_SAAS
        ? this.$t('billing.addAnother.events')
        : this.$t('billing.addAnother.sessions');
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan']),
    addDefaultPlan() {
      const defaultCapacity = _.get(this.allCapacityOrderList, [0]);
      const { plan_code } = _.get(this.capacityMap, [defaultCapacity, 0], {});
      plan_code && this.updateSelectPlan({ plan_code, cartInfo: { selected_number: 1 } });
    },
    removePlans() {
      this.batchRemovePlan(this.selectedPlanList);
    },
    mx_getMaxHosts() {
      const targetPlans = this.planMapFlat.filter((item) => (item.type === planTypes.ZOOM_MEETING) & (item.category === ZOOM_MEETING_SUB_TYPES.BIZ));
      const { max } = _.get(targetPlans, '0', { max: 1, min: 99 }) || 250;
      if (isBuyingWnE(this.getSelectedPlans)) return max;
      return this.basePlanTotalLicenses || 999;
    },
    mx_getIncreaseTips(capacity, license) {
      const currentCapacityPlanCount = getPlanTotalLicense(_.filter(this.selectedPlanList,(p) => p[this.configCapacityKey] === capacity)) || license;
      return currentCapacityPlanCount >= this.mx_getMaxHosts() ? this.$t('billing.licences_limit_tips', [this.title, this.$t(this.currentBasePlan.typeNameKey)]) : '';
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      const annualAdditionalPlan = _.find(this.getSelectedPlans, (p) => {
        return ZOOM_SASS_ADDITIONAL_CATEGORIES.includes(p.type) && p.billing_cycle === BILLING_CYCLE.ANNUAL;
      });

      const monthlyPlan = _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);

      const defaultDisabledBillingCycle = {
        disabled: true,
        disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]),
      }

      if (monthlyPlan && annualAdditionalPlan) {
        return {
          plan: monthlyPlan,
          disabled: !!annualAdditionalPlan,
          tooltips: this.$t('billing.planform.monthly_as_base_disable_tooltip', [
            this.$t(getPlanTypeName(annualAdditionalPlan?.type)),
            this.title,
          ])
        }
      }

      return { plan: monthlyPlan, ...(monthlyPlan ? {} : { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, ...defaultDisabledBillingCycle })  };
    },
    mx_onBasePlanUpdated(basePlanName) {
      this.mx_showMessage(
        this.$t('billing.license_sync_base', [basePlanName,this.title || this.$t('billing.planform.title.zoom_events')]),
      );
    },
    mx_onSelectedPlanUpdated(basePlanName) {
      this.mx_showMessage(
        this.$t('billing.license_sync_base', [this.title || this.$t('billing.planform.title.zoom_events'),basePlanName]),
      );
    },
    handleLicenseChange(license){
        if (!this.mx_canAutoAdd) return;
        this.mx_handlePlanLicenseChange(license)
    }
  },
  watch:{
    getSelectedPlans: {
      immediate: true,
      handler(n,o) {
        if(n?.length > o?.length ){
          if (!this.mx_canAutoAdd) return;
          const num = this.findUpdatedPlan(n,o)
          num && this.mx_handlePlanLicenseChange(num)
        }
      },
    },
  }
};

export default {
  mixins: [plansMixin, planCommonMixin,autoAddMixin],
  components: {
    ConfigConsumptionRow,
    ConfigBase: {
      mixins: [ConfigBase, planCommonMixin],
    },
    SaasCapacityConfigList,
  },
  data() {
    return {
      isExpanded: true,
      hasSelectedPlan: false,
      saasMode: '',
      saasModeList: [],
      EventModeMap,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    saasPlans() {
      return _.filter(this.planList, (p) => p.isSaas);
    },
    consumptionPlans() {
      return _.filter(this.planList, (p) => p.isPPA);
    },
    isSassPlansAvailable() {
      const not = this.saasPlans?.length === 0 || pageConfig.from === '';
      return !not;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'batchRemovePlan','updateSelectPlanByCategory']),
    initSaasMode() {
      this.saasModeList = [
        {
          label: EventModeMap.PAY_PER_ATTENDEE,
          name: this.$t('billing.events.ppa.title'),
        },
      ];

      if (this.isSassPlansAvailable) {
        this.saasModeList.push({
          label: EventModeMap.SUBSCRIPTION,
          name: this.$t('billing.events.saas.title'),
        });
      }

      const selectedPlans = this.getTypeGroupSelectedPlans[this.planType];
      const saasPlans = _.filter(selectedPlans, (p) => p.isSaas);
      const consumptionPlans = _.filter(selectedPlans, (p) => p.isPPA);

      if (saasPlans.length) {
        this.saasMode = EventModeMap.SUBSCRIPTION;
      } else if (consumptionPlans.length) {
        this.saasMode = EventModeMap.PAY_PER_ATTENDEE;
      }
    },
  },
  created() {
    this.initSaasMode();
  },
};
</script>
